import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Card, CardBody, Input, Page, Table, useToast } from "@theg1group/ui";
import type { TableColumn } from "@theg1group/ui";
import { useState } from "react";
import { api } from "../lib/api.ts";

interface Mailbox {
	id: string;
	address: string;
	provider: string;
	label: string | null;
	imap_host: string | null;
	imap_port: number | null;
	webmail_url: string | null;
	cred_ref: string | null;
	access_method: string;
	enabled: boolean;
	status: string;
	last_collected_at: string | null;
	observation_count: number;
}

const PROVIDERS = [
	{ v: "gmail", host: "imap.gmail.com", port: 993, url: "https://mail.google.com" },
	{ v: "outlook", host: "outlook.office365.com", port: 993, url: "https://outlook.live.com/mail" },
	{ v: "yahoo", host: "imap.mail.yahoo.com", port: 993, url: "https://mail.yahoo.com" },
	{ v: "icloud", host: "imap.mail.me.com", port: 993, url: "https://www.icloud.com/mail" },
	{ v: "gmx", host: "imap.gmx.net", port: 993, url: "https://www.gmx.net" },
	{ v: "webde", host: "imap.web.de", port: 993, url: "https://web.de" },
	{ v: "tonline", host: "secureimap.t-online.de", port: 993, url: "https://email.t-online.de" },
	{ v: "proton", host: "127.0.0.1", port: 1143, url: "https://mail.proton.me" },
	{ v: "tuta", host: "", port: 0, url: "https://app.tuta.com" },
	{ v: "other", host: "", port: 993, url: "" },
];

const blank = {
	address: "",
	provider: "gmail",
	label: "",
	imap_host: "imap.gmail.com",
	imap_port: 993,
	webmail_url: "https://mail.google.com",
	cred_ref: "",
	access_method: "imap",
	enabled: true,
};

export function Mailboxes() {
	const qc = useQueryClient();
	const toast = useToast();
	const [form, setForm] = useState({ ...blank });
	const [showAdd, setShowAdd] = useState(false);

	const list = useQuery({
		queryKey: ["mailboxes"],
		queryFn: () => api.get<{ rows: Mailbox[] }>("/api/mailboxes"),
	});

	const create = useMutation({
		mutationFn: () => api.post("/api/mailboxes", form),
		onSuccess: () => {
			toast.addToast("success", "Mailbox added");
			setForm({ ...blank });
			setShowAdd(false);
			qc.invalidateQueries({ queryKey: ["mailboxes"] });
		},
		onError: (e) => toast.addToast("error", String(e)),
	});
	const toggle = useMutation({
		mutationFn: (m: Mailbox) => api.patch(`/api/mailboxes/${m.id}`, { enabled: !m.enabled }),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["mailboxes"] }),
	});
	const del = useMutation({
		mutationFn: (id: string) => api.del(`/api/mailboxes/${id}`),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["mailboxes"] }),
	});

	function pickProvider(v: string) {
		const p = PROVIDERS.find((x) => x.v === v);
		setForm((f) => ({
			...f,
			provider: v,
			imap_host: p?.host ?? f.imap_host,
			imap_port: p?.port ?? f.imap_port,
			webmail_url: p?.url ?? f.webmail_url,
			access_method: v === "tuta" ? "webmail_only" : v === "proton" ? "imap" : f.access_method,
		}));
	}

	const columns: TableColumn<Mailbox>[] = [
		{ id: "address", header: "Address", accessor: (m) => <span className="font-mono text-xs">{m.address}</span> },
		{ id: "provider", header: "Provider", accessor: (m) => <Badge variant="info">{m.provider}</Badge> },
		{ id: "access", header: "Access", accessor: (m) => m.access_method },
		{ id: "cred", header: "1Password ref", accessor: (m) => (m.cred_ref ? <span className="font-mono text-xs text-muted">{m.cred_ref}</span> : "—") },
		{ id: "obs", header: "Observations", accessor: (m) => m.observation_count },
		{
			id: "last",
			header: "Last collected",
			accessor: (m) => (m.last_collected_at ? new Date(m.last_collected_at).toLocaleString() : "—"),
		},
		{
			id: "enabled",
			header: "Enabled",
			accessor: (m) => (
				<button type="button" onClick={() => toggle.mutate(m)} className="cursor-pointer">
					<Badge variant={m.enabled ? "success" : "default"}>{m.enabled ? "on" : "off"}</Badge>
				</button>
			),
		},
		{
			id: "actions",
			header: "",
			accessor: (m) => (
				<Button size="sm" variant="danger" onClick={() => del.mutate(m.id)}>
					Delete
				</Button>
			),
		},
	];

	return (
		<Page
			title="Mailboxes"
			description="Тестовые ящики по провайдерам. Реальный спам приходит сюда органично; локальный сборщик снимает папку и рендер вебмейла. Пароли не тут — только ссылка на 1Password."
			actions={
				<Button variant="primary" onClick={() => setShowAdd((v) => !v)}>
					{showAdd ? "Close" : "Add mailbox"}
				</Button>
			}
		>
			{showAdd && (
				<Card className="mb-4">
					<CardBody>
						<div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}>
							<Field label="Address">
								<Input
									value={form.address}
									placeholder="seed1@gmail.com"
									onChange={(e) => setForm({ ...form, address: e.target.value })}
								/>
							</Field>
							<Field label="Provider">
								<select
									value={form.provider}
									onChange={(e) => pickProvider(e.target.value)}
									className="w-full rounded-md border border-line bg-surface px-2 py-1.5 text-sm text-content"
								>
									{PROVIDERS.map((p) => (
										<option key={p.v} value={p.v}>
											{p.v}
										</option>
									))}
								</select>
							</Field>
							<Field label="Label">
								<Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
							</Field>
							<Field label="1Password ref (item name)">
								<Input
									value={form.cred_ref}
									placeholder="mbx-gmail-seed1"
									onChange={(e) => setForm({ ...form, cred_ref: e.target.value })}
								/>
							</Field>
							<Field label="IMAP host">
								<Input value={form.imap_host} onChange={(e) => setForm({ ...form, imap_host: e.target.value })} />
							</Field>
							<Field label="IMAP port">
								<Input
									type="number"
									value={String(form.imap_port)}
									onChange={(e) => setForm({ ...form, imap_port: Number(e.target.value) })}
								/>
							</Field>
							<Field label="Webmail URL">
								<Input value={form.webmail_url} onChange={(e) => setForm({ ...form, webmail_url: e.target.value })} />
							</Field>
							<Field label="Access method">
								<select
									value={form.access_method}
									onChange={(e) => setForm({ ...form, access_method: e.target.value })}
									className="w-full rounded-md border border-line bg-surface px-2 py-1.5 text-sm text-content"
								>
									<option value="imap">imap</option>
									<option value="imap_oauth">imap_oauth</option>
									<option value="webmail_only">webmail_only</option>
								</select>
							</Field>
						</div>
						<div className="mt-3">
							<Button
								variant="primary"
								loading={create.isPending}
								disabled={!form.address.trim()}
								onClick={() => create.mutate()}
							>
								Save mailbox
							</Button>
						</div>
					</CardBody>
				</Card>
			)}

			<Card>
				<div className="overflow-x-auto">
					<Table
						columns={columns}
						data={list.data?.rows ?? []}
						keyExtractor={(m) => m.id}
						loading={list.isLoading}
						emptyMessage="No mailboxes yet — add your test addresses above."
					/>
				</div>
			</Card>
		</Page>
	);
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div>
			<div className="text-[11px] text-muted mb-1">{label}</div>
			{children}
		</div>
	);
}
